import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatPoNumber } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { Toolbar } from '../ui/Page';
import { SearchInput } from '../ui/SearchInput';
import { DataTable, dash, type DataTableColumn } from '@/components/ui/data-table';
import { StatusBadge, type Tone } from '../ui/Badge';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  worstError,
  worstState,
} from '../ui/QueryStates';
import { money } from '../money';
import { PoBuilderSheet } from './PoBuilderSheet';
import type { OnHandItem, StockLocation } from './types';
import { IconInventory, IconPlus } from '../icons';

type PoRow = {
  po: {
    id: string;
    poNumber: number;
    status: string;
    source: string;
    subtotalCents: number;
    expectedDate: string | null;
    createdAt: string | Date;
  };
  vendorName: string;
  locationName: string;
};

/** Plain English for a five-state machine — nobody outside the code says "partially_received". */
const PO_STATUS: Record<string, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  sent: { label: 'Ordered', tone: 'info' },
  partially_received: { label: 'Part arrived', tone: 'warn' },
  closed: { label: 'Received', tone: 'success' },
  canceled: { label: 'Canceled', tone: 'neutral' },
};

export function PurchaseOrdersTab({
  items,
  locations,
}: {
  items: OnHandItem[];
  locations: StockLocation[];
}) {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const orders = useQuery(trpc.purchasing.list.queryOptions());
  const vendors = useQuery(trpc.purchasing.vendors.queryOptions());
  const [search, setSearch] = useState('');
  const [building, setBuilding] = useState<'order' | 'counter' | null>(null);

  const columns: DataTableColumn<PoRow>[] = [
    {
      key: 'number',
      label: 'PO',
      value: (r) => r.po.poNumber,
      searchValue: (r) => formatPoNumber(r.po.poNumber),
      cell: (r) => (
        <div>
          <p className="font-medium">{formatPoNumber(r.po.poNumber)}</p>
          {r.po.source === 'counter' ? (
            <p className="text-xs text-muted-foreground">Counter run</p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'vendor',
      label: 'Vendor',
      value: (r) => r.vendorName,
      cell: (r) => r.vendorName,
    },
    {
      key: 'status',
      label: 'Status',
      value: (r) => r.po.status,
      cell: (r) => {
        const s = PO_STATUS[r.po.status] ?? {
          label: r.po.status,
          tone: 'neutral' as Tone,
        };
        return <StatusBadge tone={s.tone}>{s.label}</StatusBadge>;
      },
    },
    {
      key: 'goes',
      label: 'Goes to',
      value: (r) => r.locationName,
      cell: (r) => r.locationName,
    },
    {
      key: 'expected',
      label: 'Expected',
      value: (r) => r.po.expectedDate,
      cell: (r) => dash(r.po.expectedDate),
    },
    {
      key: 'total',
      label: 'Total',
      align: 'right',
      value: (r) => r.po.subtotalCents,
      searchValue: () => null,
      cell: (r) => <span className="tabular-nums">{money(r.po.subtotalCents)}</span>,
    },
  ];

  /**
   * BOTH queries, because both decide what this tab says.
   *
   * 🔴 `orders.isError` was branched; `vendors` was branched nowhere. So while the vendor
   * list was loading — or had failed — `noVendors` was true, both create buttons were
   * disabled, and the tab printed "Add a vendor first" to a shop with forty vendors. That
   * is not a disabled control, it is an instruction to go and do something already done.
   *
   * Gating on both is right rather than merely convenient: with no vendor list the buttons
   * are unusable anyway, so drawing the orders table beside a false instruction would show
   * a tab that cannot be acted on while implying it can.
   */
  const state = worstState(
    shownState(orders, orders.data),
    shownState(vendors, vendors.data),
  );
  if (state !== 'ready') {
    return (
      <UnresolvedState
        state={state}
        what="your purchase orders"
        icon={IconInventory}
        // A refusal wins over a bad day — see `worstError`.
        error={worstError(orders.error, vendors.error)}
        onRetry={() => {
          void orders.refetch();
          void vendors.refetch();
        }}
      />
    );
  }

  const noVendors = (vendors.data?.length ?? 0) === 0;

  return (
    <>
      <StaleNotice
        show={refreshFailed(orders, orders.data) || refreshFailed(vendors, vendors.data)}
        onRetry={() => {
          void orders.refetch();
          void vendors.refetch();
        }}
      />
      <Toolbar>
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search orders"
          aria-label="Search purchase orders"
        />
        <div className="ml-auto flex gap-2">
          {/* The counter run comes first because it's the one that happens without warning
              — a tech at the supply house, not an owner at a desk. */}
          <Button
            variant="ghost"
            disabled={noVendors}
            onClick={() => setBuilding('counter')}
          >
            Bought at the counter
          </Button>
          <Button disabled={noVendors} onClick={() => setBuilding('order')}>
            <IconPlus size={14} />
            New order
          </Button>
        </div>
      </Toolbar>

      {noVendors ? (
        <p className="text-xs text-muted-foreground">
          Add a vendor first — the Vendors tab. An order has to be to somebody.
        </p>
      ) : null}

      <DataTable
        tableId="purchase-orders"
        columns={columns}
        rows={(orders.data ?? []) as PoRow[]}
        rowKey={(r) => r.po.id}
        search={search}
        onRowClick={(r) =>
          void navigate({
            to: '/purchase-orders/$purchaseOrderId',
            params: { purchaseOrderId: r.po.id },
          })
        }
        rowLabel={(r) => `Open ${formatPoNumber(r.po.poNumber)}`}
        empty={
          <EmptyState
            icon={IconInventory}
            title="No purchase orders yet"
            description="Order parts from a vendor, or record a counter run you already paid for — both land here."
          />
        }
      />

      {building ? (
        <PoBuilderSheet
          mode={building}
          items={items}
          locations={locations}
          vendors={vendors.data ?? []}
          onClose={() => setBuilding(null)}
          onCreated={(id) =>
            void navigate({
              to: '/purchase-orders/$purchaseOrderId',
              params: { purchaseOrderId: id },
            })
          }
        />
      ) : null}
    </>
  );
}
